import {ActionIcon, MultiSelect, Stack, Text, TextInput, Title} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {IconSearch, IconX} from '@tabler/icons-react'
import {useNavigate} from '@tanstack/react-router'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'

import {formatPrice} from '../lib/format-price'
import {InvoiceStatusBadge} from './invoice-status-badge'
import type {InvoiceListItem} from './schemas'
import {useInvoices} from './use-invoices'

type InvoiceStatus = 'draft' | 'invoiced' | 'void'

const STATUS_FILTER_OPTIONS: Array<{value: InvoiceStatus; label: string}> = [
    {value: 'draft', label: 'Draft'},
    {value: 'invoiced', label: 'Invoiced'},
    {value: 'void', label: 'Void'},
]

function formatCreatedAt(createdAt: string) {
    return formatBusinessTimestamp(createdAt, 'MMM d, yyyy')
}

function invoiceStatus(invoice: InvoiceListItem): InvoiceStatus {
    if (invoice.isVoided) {
        return 'void'
    }
    if (invoice.isDraft) {
        return 'draft'
    }
    return 'invoiced'
}

function columnFilterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

function compareInvoiceStatus(left: InvoiceStatus, right: InvoiceStatus) {
    const order: Record<InvoiceStatus, number> = {
        draft: 0,
        invoiced: 1,
        void: 2,
    }
    return order[left] - order[right]
}

function compareInvoices(
    left: InvoiceListItem,
    right: InvoiceListItem,
    columnAccessor: DataTableSortStatus<InvoiceListItem>['columnAccessor'],
) {
    if (columnAccessor === 'isDraft') {
        return compareInvoiceStatus(invoiceStatus(left), invoiceStatus(right))
    }

    if (columnAccessor === 'total' || columnAccessor === 'lineItemCount') {
        return left[columnAccessor] - right[columnAccessor]
    }

    if (columnAccessor === 'createdAt') {
        return left.createdAt.localeCompare(right.createdAt)
    }

    const leftValue = String(left[columnAccessor as keyof InvoiceListItem])
    const rightValue = String(right[columnAccessor as keyof InvoiceListItem])
    return leftValue.localeCompare(rightValue)
}

function sortInvoices(
    invoices: InvoiceListItem[],
    sortStatus: DataTableSortStatus<InvoiceListItem>,
) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...invoices].sort(
        (left, right) =>
            directionMultiplier * compareInvoices(left, right, sortStatus.columnAccessor),
    )
}

export function InvoicesPage() {
    const navigate = useNavigate()
    const {data: invoices = [], isLoading, isError, error} = useInvoices()
    const [projectNameFilter, setProjectNameFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState<InvoiceStatus[]>([])
    const [debouncedProjectName] = useDebouncedValue(projectNameFilter, 300)
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<InvoiceListItem>>({
        columnAccessor: 'createdAt',
        direction: 'desc',
    })

    const filtersActive = Boolean(debouncedProjectName.trim() || statusFilter.length > 0)

    const filteredInvoices = useMemo(() => {
        const normalizedProjectName = debouncedProjectName.trim().toLowerCase()

        return invoices.filter((invoice) => {
            if (
                normalizedProjectName &&
                !invoice.projectName.toLowerCase().includes(normalizedProjectName)
            ) {
                return false
            }

            if (statusFilter.length > 0 && !statusFilter.includes(invoiceStatus(invoice))) {
                return false
            }

            return true
        })
    }, [debouncedProjectName, invoices, statusFilter])

    const sortedInvoices = useMemo(
        () => sortInvoices(filteredInvoices, sortStatus),
        [filteredInvoices, sortStatus],
    )

    const columns = useMemo((): DataTableColumn<InvoiceListItem>[] => {
        return [
            {
                accessor: 'projectName',
                title: 'Order',
                sortable: true,
                filter: (
                    <TextInput
                        label="Order"
                        description="Show invoices for orders whose names include the specified text"
                        placeholder="Search order names..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(projectNameFilter, () =>
                            setProjectNameFilter(''),
                        )}
                        value={projectNameFilter}
                        onChange={(event) => setProjectNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: projectNameFilter !== '',
            },
            {
                accessor: 'isDraft',
                title: 'Status',
                sortable: true,
                width: 150,
                render: ({isDraft, isVoided}) => (
                    <InvoiceStatusBadge isVoided={isVoided} isDraft={isDraft} />
                ),
                filter: (
                    <Stack w={200}>
                        <MultiSelect
                            label="Status"
                            description="Show invoices in any of the selected statuses"
                            placeholder="All statuses"
                            data={STATUS_FILTER_OPTIONS}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            clearable
                        />
                    </Stack>
                ),
                filtering: statusFilter.length > 0,
            },
            {
                accessor: 'lineItemCount',
                title: 'Line items',
                sortable: true,
                width: 100,
            },
            {
                accessor: 'poNumber',
                title: 'PO',
                sortable: true,
                filter: (
                    <TextInput
                        label="PO"
                        description="Show POs for the specified text"
                        placeholder="Search POs"
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(projectNameFilter, () =>
                            setPOFilter(''),
                        )}
                        value={projectNameFilter}
                        onChange={(event) => setProjectNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: projectNameFilter !== '',
                render: ({poNumber}) => poNumber ?? 'No PO',
            },
            {
                accessor: 'total',
                title: 'Total',
                sortable: true,
                render: ({total}) => formatPrice(total),
            },
            {
                accessor: 'createdAt',
                title: 'Created',
                sortable: true,
                render: ({createdAt}) => formatCreatedAt(createdAt),
            },
        ]
    }, [projectNameFilter, statusFilter])

    if (isError) {
        return (
            <Stack gap="md">
                <Title order={1}>Invoices</Title>
                <Text c="red">
                    {error instanceof Error ? error.message : 'Failed to load invoices'}
                </Text>
            </Stack>
        )
    }

    return (
        <Stack gap="md">
            <Stack gap={4}>
                <Title order={1}>Invoices</Title>
                <Text size="sm" c="dimmed">
                    Draft invoices group selected order charges before they are sent in QuickBooks.
                </Text>
            </Stack>

            <DataTable
                withTableBorder
                withColumnBorders
                striped
                highlightOnHover
                textSelectionDisabled
                records={sortedInvoices}
                columns={columns}
                idAccessor="id"
                fetching={isLoading}
                minHeight={sortedInvoices.length === 0 ? 180 : undefined}
                noRecordsText={
                    filtersActive ? 'No invoices match your filters.' : 'No invoices yet.'
                }
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                onRowClick={({record}) =>
                    void navigate({
                        to: '/invoices/$invoiceId',
                        params: {invoiceId: record.id},
                    })
                }
            />
        </Stack>
    )
}
